import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SavingsIcon from "@mui/icons-material/Savings";
import RepeatIcon from "@mui/icons-material/Repeat";
import PaymentsIcon from "@mui/icons-material/Payments";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import { SimulationChangeType, SimulationMimshakData } from "../../../interfaces/DTO/simulation";
import { colorSimulationChangeType, formatSimulationChangeType } from "../../../utils/boardTemplates";
import { simulationService } from "../../../services/SimulationService";
import { useSnackbarStore, SnackbarType } from "../../../stores/useSnackbarStore";
import { useContacts } from "../../../hooks/crm/useContacts";
import { formatName } from "../../../utils/namesFormater";
import { taskFormService } from "../../../services/forms/taskFormService";
import PreparedFormsStep from "../FormsPreparation/PreparedForms/PreparedFormsStep";
import { PreparedFormItem, usePreparedForms } from "../FormsPreparation/PreparedForms/usePreparedForms";
import SimulationLinkSendStep from "./SimulationLinkSendStep";
import TransferWizard from "./TransferWizard";
import AmountActionForm from "./AmountActionForm";
import PathChangeForm from "./PathChangeForm";
import ViewPowerOfAttorneyForm from "./ViewPowerOfAttorneyForm";
import {
  QuickAction,
  actionContactId,
  buildSimulationItems,
  describeAction,
  isBasketNewFund,
  isInvestmentPortfolio,
  policiesAfterActions,
  policyKey,
  policyLabel,
  removeActionWithDependents,
} from "./quickActions";

type ActionMode = "transfer" | "one_time_deposit" | "recurring_deposit" | "withdrawal" | "path_change" | "view_power_of_attorney";
/**
 * menu - the basket ("מה עוד ללקוח זה?"), forms - preparing the producers' forms (a new standing order), title -
 * naming the simulation before creating it
 */
type Mode = "menu" | "forms" | "title" | ActionMode;

// the color is the action's change type color (as the lab's change chips)
const ACTION_BUTTONS: { mode: ActionMode; label: string; icon: React.ReactElement; changeType: SimulationChangeType }[] = [
  { mode: "transfer", label: "ניוד", icon: <SwapHorizIcon />, changeType: SimulationChangeType.CLOSE_AND_MOVE },
  { mode: "path_change", label: "שינוי מסלול", icon: <AltRouteIcon />, changeType: SimulationChangeType.CHANGE_FUND },
  { mode: "one_time_deposit", label: "הפקדה חד פעמית", icon: <SavingsIcon />, changeType: SimulationChangeType.DEPOSIT },
  { mode: "recurring_deposit", label: "הוראת קבע", icon: <RepeatIcon />, changeType: SimulationChangeType.CHANGE_RECURRENT_DEPOSIT },
  { mode: "withdrawal", label: "משיכה", icon: <PaymentsIcon />, changeType: SimulationChangeType.WITHDRAWAL },
  { mode: "view_power_of_attorney", label: "ייפוי כוח לצפייה", icon: <VisibilityIcon />, changeType: SimulationChangeType.VIEW_POWER_OF_ATTORNEY },
];

const MODE_TITLE: Record<ActionMode, string> = Object.fromEntries(ACTION_BUTTONS.map((b) => [b.mode, b.label])) as Record<
  ActionMode,
  string
>;

interface Props {
  open: boolean;
  onClose: () => void;
  /** the policies shown in the table (after its filters) - the ones offered */
  policies: MimshakData[];
  /** all the client's / family's policies - offered after "הצג הכל" (when the table is filtered) */
  allPolicies?: MimshakData[];
  onCreated?: () => void;
}

/**
 * פעולות מהירות ללקוח – בלי להיכנס למעבדה: בוחרים פעולה, ובסופה "מה עוד ללקוח זה?".
 * הכל נשמר כסימולציה אחת עם משימה (ו-workflow מלא) לכל שינוי.
 * הפעולות של סל אחד הן של לקוח אחד (לסימולציה יש contact_id אחד) – בתצוגת משפחה, אחרי הפעולה הראשונה
 * מוצגות רק הקופות של אותו בן משפחה.
 */
const ClientActionsDialog: React.FC<Props> = ({ open, onClose, policies: filteredPolicies, allPolicies, onCreated }) => {
  const [mode, setMode] = useState<Mode>("menu");
  const [showAll, setShowAll] = useState(false);
  const isFiltered = !!allPolicies && allPolicies.length > filteredPolicies.length;
  const policies = showAll && allPolicies ? allPolicies : filteredPolicies;
  const [actions, setActions] = useState<QuickAction[]>([]);
  // why the request was made - the simulation's title (empty - an automatic title from the actions)
  const [simulationTitle, setSimulationTitle] = useState("");
  const [titleChoice, setTitleChoice] = useState<"suggested" | "custom">("suggested");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdSimulationId, setCreatedSimulationId] = useState<number | null>(null);
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);
  const { data: contacts = [] } = useContacts();
  const preparedForms = usePreparedForms();

  const basketContactId = actions.length > 0 ? actionContactId(actions[0]) : null;
  const basketContact = contacts.find((c) => c.id === basketContactId);
  const isFamily = new Set(policies.map((p) => p.contact_id)).size > 1;

  const availablePolicies = useMemo(() => {
    const scoped = basketContactId === null ? policies : policies.filter((p) => p.contact_id === basketContactId);
    return policiesAfterActions(scoped, actions);
  }, [policies, actions, basketContactId]);

  const showAllToggle = isFiltered ? (
    <Box sx={{ textAlign: "center", py: 1 }}>
      <Button size="small" onClick={() => setShowAll((v) => !v)}>
        {showAll ? "הצג רק את המסונן בטבלה" : "הצג הכל"}
      </Button>
    </Box>
  ) : null;

  const reset = () => {
    setMode("menu");
    setShowAll(false);
    setActions([]);
    setError(null);
    setCreatedSimulationId(null);
    setSimulationTitle("");
    setTitleChoice("suggested");
    preparedForms.reset();
    setFormItems([]);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const addAction = (action: QuickAction) => {
    setActions((prev) => [...prev, action]);
    setMode("menu");
  };

  // removing an action removes the ones that depend on it (an action on a new fund that is no longer opened)
  const removeAction = (id: string) => setActions((prev) => removeActionWithDependents(prev, id));

  // a new fund for a deposit / standing order - of the basket's client, or (an empty basket) of a family member
  const newFundContacts = useMemo(() => {
    const ids = basketContactId !== null ? [basketContactId] : Array.from(new Set(policies.map((p) => p.contact_id)));
    return ids.map((id) => {
      const contact = contacts.find((c) => c.id === id);
      return { id, name: contact ? formatName(contact) : String(id) };
    });
  }, [basketContactId, policies, contacts]);

  // the basket's forms step: its items (as they will be created) and the tasks of theirs that prepare forms
  const [formItems, setFormItems] = useState<(PreparedFormItem & { item: SimulationMimshakData; taskType: string })[]>([]);
  const [loadingForms, setLoadingForms] = useState(false);

  /**
   * After the basket: the forms of the tasks it will create whose workflow prepares forms (a transfer, a new fund,
   * a path change, a withdrawal, a new standing order ...) - each pre-filled from its item, or "prepared in an
   * external system" when the system has no form for it (as the beneficiaries update). Then the title.
   */
  const finishBasket = async () => {
    if (basketContactId === null) return;
    const items = buildSimulationItems(actions);
    setLoadingForms(true);
    try {
      const drafts = await taskFormService.drafts(basketContactId, items);
      const next = drafts.map((d) => {
        const item = items[d.item_index];
        return {
          key: `${d.item_index}:${d.change_type}`,
          label: `${formatSimulationChangeType(d.change_type as SimulationChangeType)} · ${policyLabel(item)}`,
          draft: () => Promise.resolve(d.form),
          item,
          taskType: d.task_type,
        };
      });
      setFormItems(next);
      if (drafts.length > 0) {
        preparedForms.load(next);
        setMode("forms");
        return;
      }
    } catch (e) {
      console.error("Failed to prepare the basket's forms:", e);   // prepared later, from the tasks
      setFormItems([]);
    } finally {
      setLoadingForms(false);
    }
    preparedForms.reset();
    setMode("title");
  };

  const autoTitle = actions.length === 1 ? describeAction(actions[0]) : `פעולות מהירות (${actions.length})`;
  // the simulation's title - shown to the client on the personal link
  const chosenTitle = titleChoice === "custom" ? simulationTitle.trim() : autoTitle;

  const handleSubmit = async () => {
    if (actions.length === 0 || basketContactId === null || !chosenTitle) return;
    setSubmitting(true);
    setError(null);
    try {
      const firstPolicy = actions[0].kind === "transfer" ? actions[0].sources[0] : actions[0].policy;
      const response = await simulationService.quickCreate({
        simulation: {
          contact_id: basketContactId,
          reported_period: firstPolicy.reported_period,
          name: chosenTitle,
          is_pre_advice: false,
        },
        items: buildSimulationItems(actions),
        create_tasks: true,
        source: "quickTaskAction",
        // no selected_step_codes - every task gets its workflow's full steps (the basket mixes workflows)
      });
      // the forms prepared now - saved on their tasks (the item's task of that type) and sent to the client for
      // signature; one not saved is prepared later, from the task
      const failedForms = await preparedForms.saveOnTasks((key) => {
        const formItem = formItems.find((f) => f.key === key);
        if (!formItem) return undefined;
        return (response.created_tasks ?? []).find(
          (t) => t.policy_number === formItem.item.policy_number && t.handler_code === formItem.item.handler_code
            && t.task_type === formItem.taskType
        )?.id;
      });
      const formsSent = preparedForms.preparedKeys.length - failedForms.length;
      showSnackbar(
        response.tasks_created === 0
          ? "הסימולציה נוצרה אך לא נוצרו משימות"
          : failedForms.length
          ? `נוצרו ${response.tasks_created} משימות. ${failedForms.length} טפסים לא נשמרו - אפשר להכין אותם מעזרת הכנת הטפסים במשימה.`
          : `נוצרו ${response.tasks_created} משימות${formsSent ? ` · ${formsSent} טפסים נשלחו לחתימה` : ""}`,
        response.tasks_created === 0 ? SnackbarType.ERROR : failedForms.length ? SnackbarType.WARNING : SnackbarType.SUCCESS
      );
      onCreated?.();
      if (response.simulation?.id != null) {
        setCreatedSimulationId(response.simulation.id);
      } else {
        handleClose();
      }
    } catch (e) {
      console.error("Failed to create quick actions:", e);
      setError("שגיאה ביצירת המשימות. נסה שנית.");
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    createdSimulationId != null
      ? "המשימות נוצרו"
      : mode === "title"
      ? "תן כותרת"
      : mode === "forms"
      ? "הכנת הטפסים"
      : mode !== "menu"
      ? MODE_TITLE[mode]
      : actions.length > 0
      ? "מה עוד ללקוח זה?"
      : "איזו פעולה?";

  const backToMenu = () => setMode("menu");
  // an investment portfolio (loaded by hand) - only a view power of attorney, not the fund actions
  const fundPolicies = availablePolicies.filter((p) => !isInvestmentPortfolio(p));
  // a fund with money, that is not a new fund of this basket - can be withdrawn from
  const withdrawablePolicies = fundPolicies.filter((p) => (p.funds_sum || 0) > 0 && !isBasketNewFund(p));
  // a real policy / portfolio without a view power of attorney in the basket yet
  const powerOfAttorneyPolicies = availablePolicies.filter(
    (p) => !isBasketNewFund(p)
      && !actions.some((a) => a.kind === "view_power_of_attorney" && policyKey(a.policy) === policyKey(p))
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth dir="rtl">
      <IconButton
        onClick={handleClose}
        disabled={submitting}
        size="small"
        aria-label="סגור"
        sx={{ position: "absolute", left: 12, top: 12 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <DialogTitle sx={{ pl: 6 }}>
        <Typography variant="h6" fontWeight={700} component="span" sx={{ display: "block" }}>
          {title}
          {mode === "title" && createdSimulationId == null && (
            <Typography component="span" variant="body1" color="text.secondary">
              {" "}
              (תוצג ללקוח בלינק האישי)
            </Typography>
          )}
        </Typography>
        {isFamily && basketContact && (
          <Typography variant="body2" color="text.secondary">
            פעולות עבור {formatName(basketContact)}
          </Typography>
        )}
      </DialogTitle>

      {createdSimulationId != null ? (
        <SimulationLinkSendStep
          simulationId={createdSimulationId}
          contact={basketContact}
          title={chosenTitle}
          actionsCount={actions.length}
          onClose={handleClose}
        />
      ) : mode === "forms" ? (
        <>
          <DialogContent>
            <PreparedFormsStep items={formItems} forms={preparedForms} disabled={submitting} />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={backToMenu} variant="outlined" size="large">
              חזור
            </Button>
            <Button onClick={() => setMode("title")} disabled={!preparedForms.ready} variant="contained" size="large">
              המשך
            </Button>
          </DialogActions>
        </>
      ) : mode === "title" ? (
        <>
          <DialogContent>
            <RadioGroup value={titleChoice} onChange={(e) => setTitleChoice(e.target.value as "suggested" | "custom")}>
              <FormControlLabel
                value="suggested"
                control={<Radio />}
                disabled={submitting}
                label={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      כותרת מוצעת
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {autoTitle}
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: "flex-start", mb: 2, "& .MuiRadio-root": { pt: 0.5 } }}
              />
              <FormControlLabel
                value="custom"
                control={<Radio />}
                disabled={submitting}
                label="כותרת אחרת"
                sx={{ mb: 1 }}
              />
            </RadioGroup>
            <TextField
              value={simulationTitle}
              onChange={(e) => setSimulationTitle(e.target.value)}
              // typing here is choosing "כותרת אחרת"
              onFocus={() => setTitleChoice("custom")}
              placeholder="למשל: איחוד קופות גמל והורדת דמי ניהול"
              fullWidth
              multiline
              maxRows={3}
              disabled={submitting}
            />

            {error && (
              <Alert severity="error" sx={{ mt: 2, fontSize: 12 }}>
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button onClick={() => setMode(Object.keys(preparedForms.forms).length > 0 ? "forms" : "menu")} disabled={submitting} variant="outlined" size="large">
              חזור
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!chosenTitle || submitting}
              variant="contained"
              size="large"
              startIcon={submitting ? <CircularProgress size={14} /> : undefined}
            >
              {preparedForms.preparedKeys.length > 0 ? `צור משימות ושלח ${preparedForms.preparedKeys.length} טפסים לחתימה` : "צור משימות"}
            </Button>
          </DialogActions>
        </>
      ) : mode === "transfer" ? (
        <TransferWizard policies={fundPolicies} onComplete={addAction} onCancel={backToMenu} policiesFooter={showAllToggle} />
      ) : mode === "path_change" ? (
        <PathChangeForm policies={fundPolicies} onComplete={addAction} onCancel={backToMenu} policiesFooter={showAllToggle} />
      ) : mode === "view_power_of_attorney" ? (
        <ViewPowerOfAttorneyForm
          policies={powerOfAttorneyPolicies}
          onComplete={addAction}
          onCancel={backToMenu}
          policiesFooter={showAllToggle}
        />
      ) : mode === "one_time_deposit" || mode === "recurring_deposit" || mode === "withdrawal" ? (
        <AmountActionForm
          key={mode}
          kind={mode}
          policies={mode === "withdrawal" ? withdrawablePolicies : fundPolicies}
          newFundContacts={newFundContacts}
          onComplete={addAction}
          onCancel={backToMenu}
          policiesFooter={showAllToggle}
        />
      ) : (
        <>
          <DialogContent>
            {actions.length > 0 && (
              <List dense sx={{ mb: 1, bgcolor: "action.hover", borderRadius: 2 }}>
                {actions.map((a) => (
                  <ListItem
                    key={a.id}
                    secondaryAction={
                      <IconButton edge="end" size="small" onClick={() => removeAction(a.id)} disabled={submitting}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    }
                  >
                    <ListItemText primary={describeAction(a)} slotProps={{ primary: { style: { textAlign: "right" } } }} />
                  </ListItem>
                ))}
              </List>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 1 }}>
              {ACTION_BUTTONS.map((b) => (
                <Chip
                  key={b.mode}
                  icon={b.icon}
                  label={b.label}
                  clickable
                  onClick={() => setMode(b.mode)}
                  disabled={submitting}
                  sx={{
                    backgroundColor: colorSimulationChangeType(b.changeType),
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    px: 1,
                    "& .MuiChip-icon": { color: "#fff", ml: 0.5, mr: -0.5 },
                    "&:hover": { backgroundColor: colorSimulationChangeType(b.changeType), filter: "brightness(0.9)" },
                  }}
                />
              ))}
            </Box>

          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            {actions.length > 0 && isFamily && (
              <Button onClick={reset} disabled={submitting} size="large" sx={{ ml: "auto" }}>
                נקה והתחל מחדש
              </Button>
            )}
            <Button onClick={handleClose} disabled={submitting} variant="outlined" size="large">
              חזור
            </Button>
            <Button
              onClick={finishBasket}
              disabled={actions.length === 0 || loadingForms}
              variant="contained"
              size="large"
              startIcon={loadingForms ? <CircularProgress size={14} /> : undefined}
            >
              סיימתי – צור משימות
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default ClientActionsDialog;
